import type React from 'react';
import { Tooltip } from 'antd';
import { CalendarFilled, PushpinFilled } from '@ant-design/icons';
import startCase from 'lodash/startCase';
import { PROMO_STATUS_COLORS } from '../../config/calendarConstants';
import { tpoKeepFlagIndicatorTestId } from '../../config/testIds';
import type { CalendarRow } from '../../types/calendar';

const KEEP_INDICATOR_STYLE = { fontSize: 11, color: '#6B7280' };

/** Tenant wording for the three keep flags, resolved by the caller from config. */
export interface KeepIndicatorLabels {
    keepDates: string;
    keepPromo: string;
    keepEvent: string;
}

export const renderEventName = (text: string, record: CalendarRow): React.ReactNode => (
    <p className={`truncate text-sm ${record.children ? 'cursor-pointer font-semibold' : 'font-normal'}`}>{text}</p>
);

/**
 * Pin on the event cell while the whole event is kept. The flag is event-level,
 * so it renders independently of the promo cell's indicator — a row can show
 * both, one per column.
 */
export const renderKeepEventIndicator = (
    record: CalendarRow,
    title?: string,
    cellTestId?: string,
): React.ReactNode => {
    if (!record.keep_event) return null;

    return (
        <Tooltip title={title} showArrow={false}>
            <span
                data-testid={cellTestId ? tpoKeepFlagIndicatorTestId(cellTestId) : undefined}
                data-state-keep-event="true"
                className="flex-shrink-0 leading-none mr-2"
            >
                <PushpinFilled style={KEEP_INDICATOR_STYLE} />
            </span>
        </Tooltip>
    );
};

export const renderPromoName = (
    text: string,
    record: CalendarRow,
    isEditable = false,
    keepLabels?: KeepIndicatorLabels,
    cellTestId?: string,
): React.ReactNode => {
    const nameText = (
        <Tooltip title={text} showArrow={false}>
            <p className={`truncate text-sm font-normal ${isEditable ? 'text-purpleFont underline' : ''}`}>{text}</p>
        </Tooltip>
    );

    // The two promo-level flags are mutually exclusive, so at most one icon ever
    // shows. A kept event supersedes both: it pins every promo under it and
    // already says so on the event cell, so this indicator stands down.
    const keepIndicator = !record.keep_event && (record.keep_dates || record.keep_promo) ? (
        <Tooltip
            title={record.keep_dates ? keepLabels?.keepDates : keepLabels?.keepPromo}
            showArrow={false}
        >
            <span
                data-testid={cellTestId ? tpoKeepFlagIndicatorTestId(cellTestId) : undefined}
                data-state-keep-dates={record.keep_dates ? 'true' : undefined}
                data-state-keep-promo={record.keep_promo ? 'true' : undefined}
                className="flex-shrink-0 leading-none"
            >
                {record.keep_dates ? (
                    <CalendarFilled style={KEEP_INDICATOR_STYLE} />
                ) : (
                    <PushpinFilled style={KEEP_INDICATOR_STYLE} />
                )}
            </span>
        </Tooltip>
    ) : null;

    return (
        <div className="flex flex-row items-center gap-x-2 min-w-0">
            <Tooltip title={startCase(record.promo_status ?? '')} showArrow={false}>
                <div
                    className="w-2 h-2 rounded-full flex-shrink-0"
                    style={{ backgroundColor: PROMO_STATUS_COLORS[(record.promo_status ?? '').toLowerCase()] }}
                />
            </Tooltip>
            {keepIndicator}
            {nameText}
        </div>
    );
};

export const renderPlainText = (text: string): React.ReactNode => (
    <Tooltip title={text} showArrow={false}>
        <p className="truncate text-sm font-normal">{text}</p>
    </Tooltip>
);

export const renderStatus = (text: string): React.ReactNode => (
    <Tooltip title={startCase(text)} showArrow={false}>
        <p className="truncate text-sm font-normal">{startCase(text)}</p>
    </Tooltip>
);

export const CALENDAR_CELL_RENDERERS: Record<string, (text: any, record: CalendarRow) => React.ReactNode> = {
    event_name: renderEventName,
    promo_name: renderPromoName,
    promotion_mechanic: renderPromoName,
    promo_id: renderPlainText,
    event_id: renderPlainText,
    source_event_id: renderPlainText,
    source_promo_id: renderPlainText,
    offer_type: renderPlainText,
    promo_status: renderStatus,
};
